import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { IoMdArrowRoundBack, IoMdArrowRoundForward } from "react-icons/io";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import styled from "styled-components";

interface PdfPrintProps {
  fileSrc: string;
  fullscreen?: boolean;
}

export const PdfContainer = styled.div.attrs({
  className: "",
})`
  overflow: scroll;
  margin-top: 3rem;
  height: 95vh;

  @media print {
    overflow: visible;
    height: auto;
    margin-top: 0;
  }
`;

export const ModalInner = styled.div`
  position: relative;

  @media print {
    .print-only-all-pages {
      display: block !important;
    }
    .screen-only {
      display: none !important;
    }
  }

  .print-only-all-pages {
    display: none;
  }
`;

const PdfPrintViewer = ({ fileSrc, fullscreen }: PdfPrintProps) => {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const options = useMemo(
    () => ({
      cMapUrl: "cmaps/",
      cMapPacked: true,
    }),
    []
  );

  const goToPreviousPage = () => setPageNumber(Math.max(1, pageNumber - 1));
  const goToNextPage = () =>
    setPageNumber(Math.min(numPages || pageNumber, pageNumber + 1));

  const wrapperRef = useRef<HTMLDivElement>(null);
  // Natural page width in PDF points (1pt = 1.333... px @ 96dpi). Captured from
  // the first page so we can decide whether to scale down for wide/landscape PDFs.
  const [naturalPageWidthPx, setNaturalPageWidthPx] = useState<number | null>(
    null
  );
  const [maxAllowedWidth, setMaxAllowedWidth] = useState<number>(() =>
    Math.floor(window.innerWidth * 0.9)
  );

  useEffect(() => {
    if (!fullscreen) return;
    const update = () =>
      setMaxAllowedWidth(Math.floor(window.innerWidth * 0.9));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [fullscreen]);

  // Only pass an explicit width to <Page> when the PDF's natural width exceeds
  // the viewport — that way normal-width PDFs render at native size and only
  // wide/landscape ones get scaled down to fit.
  const pageWidth =
    naturalPageWidthPx && naturalPageWidthPx > maxAllowedWidth
      ? maxAllowedWidth
      : undefined;

  const onFirstPageLoad = (page: { originalWidth: number }) => {
    if (naturalPageWidthPx === null) {
      // originalWidth is in PDF points; convert to CSS pixels (96dpi).
      setNaturalPageWidthPx(Math.round(page.originalWidth * (96 / 72)));
    }
  };

  return (
    <Fragment>
      <div ref={wrapperRef} style={{ width: "100%" }}>
      <Document
        file={fileSrc}
        onLoadSuccess={onDocumentLoadSuccess}
        options={options}
        className={fullscreen ? "h-auto" : "h-auto w-max"}
      >
        {!fullscreen ? (
          <Fragment>
            {Array.from(new Array(numPages), (el, index) => (
              <Page key={`page_${index + 1}`} pageNumber={index + 1} />
            ))}
          </Fragment>
        ) : (
          <>
            <div className="screen-only">
              <Page
                pageNumber={pageNumber}
                width={pageWidth}
                onLoadSuccess={onFirstPageLoad}
              />
            </div>
            <div className="print-only-all-pages">
              {Array.from(new Array(numPages), (el, index) => (
                <Page
                  key={`print_page_${index + 1}`}
                  pageNumber={index + 1}
                  width={pageWidth}
                />
              ))}
            </div>
          </>
        )}
      </Document>
      </div>
      {fullscreen && (
        <div className="screen-only">
          <button
            onClick={goToPreviousPage}
            disabled={pageNumber <= 1}
            className="cursor-pointer"
          >
            <IoMdArrowRoundBack />
          </button>
          <button
            onClick={goToNextPage}
            disabled={pageNumber >= (numPages || 0)}
            className="cursor-pointer"
          >
            <IoMdArrowRoundForward />
          </button>
        </div>
      )}
    </Fragment>
  );
};

export default PdfPrintViewer;
